import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';

/**
 * CodeInput — 6 single-char cells with auto-advance + paste support.
 * `onChange(code)` fires whenever the concatenated value changes, including
 * intermediate partial entries (empty/partial strings). Callers should
 * guard on `code.length === 6` before submitting.
 *
 * Ref exposes `reset()` which clears all cells and refocuses the first.
 */
export const CodeInput = forwardRef(function CodeInput(
  { length = 6, onChange, value = '', initialFocus = true, error = false, ariaLabel = 'Verification code' },
  ref,
) {
  const [cells, setCells] = useState(() => splitValue(value, length));
  const inputs = useRef([]);

  useEffect(() => {
    setCells(splitValue(value, length));
  }, [value, length]);

  useEffect(() => {
    if (initialFocus && inputs.current[0]) inputs.current[0].focus();
  }, [initialFocus]);

  useImperativeHandle(ref, () => ({
    reset() {
      setCells(Array(length).fill(''));
      onChange?.('');
      if (inputs.current[0]) inputs.current[0].focus();
    },
    focus() {
      if (inputs.current[0]) inputs.current[0].focus();
    },
  }), [length, onChange]);

  const emit = (next) => {
    setCells(next);
    onChange?.(next.join(''));
  };

  const setAt = (idx, char) => {
    const next = [...cells];
    next[idx] = char;
    emit(next);
    if (char && idx < length - 1) {
      inputs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      if (cells[idx]) {
        const next = [...cells];
        next[idx] = '';
        emit(next);
        e.preventDefault();
      } else if (idx > 0) {
        const next = [...cells];
        next[idx - 1] = '';
        emit(next);
        inputs.current[idx - 1]?.focus();
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputs.current[idx - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && idx < length - 1) {
      inputs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const handleChange = (idx, e) => {
    const raw = e.target.value;
    if (raw === '') {
      const next = [...cells];
      next[idx] = '';
      emit(next);
      return;
    }
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      return;
    }
    if (digits.length === 1) {
      setAt(idx, digits);
      return;
    }
    // Multi-char entry (e.g. autofill). Distribute across subsequent cells.
    const next = [...cells];
    let cursor = idx;
    for (const d of digits) {
      if (cursor >= length) break;
      next[cursor] = d;
      cursor += 1;
    }
    emit(next);
    const last = Math.min(idx + digits.length, length - 1);
    inputs.current[last]?.focus();
  };

  const handlePaste = (idx, e) => {
    const text = e.clipboardData?.getData('text') ?? '';
    const digits = text.replace(/\D/g, '').slice(0, length);
    if (!digits) {
      e.preventDefault();
      return;
    }
    const next = [...cells];
    let cursor = idx;
    for (const d of digits) {
      if (cursor >= length) break;
      next[cursor] = d;
      cursor += 1;
    }
    emit(next);
    const last = Math.min(idx + digits.length - 1, length - 1);
    inputs.current[last]?.focus();
    e.preventDefault();
  };

  return (
    <div
      className="code-input"
      role="group"
      aria-label={ariaLabel}
    >
      {cells.map((c, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={c}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          aria-label={`Digit ${i + 1} of ${length}`}
          aria-invalid={error ? 'true' : undefined}
          className="code-input-cell"
        />
      ))}
    </div>
  );
});

function splitValue(v, length) {
  const arr = Array(length).fill('');
  const digits = (v || '').replace(/\D/g, '').slice(0, length);
  for (let i = 0; i < digits.length; i += 1) arr[i] = digits[i];
  return arr;
}
